import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Spinner from '../../components/Spinner';
import { fosError, fosFunds, formatDate, formatMoney, type DashboardView, type ProgressLine } from '../../lib/fosApi';
import { useFos } from './FosShell';
import { Kpi, Legend, ProgressBar } from './Progress';
import { EmptyState, ErrorNote, Icon, Section } from './ui';

function PercentChip({ line }: { line: ProgressLine }) {
  if (line.percent === null) return <span className="fos-chip neutral">No goal set</span>;
  return <span className={`fos-chip ${line.percent >= 100 ? 'status-active' : 'neutral'}`}>{line.percent}% of goal</span>;
}

function CompareRow({ name, line }: { name: string; line: ProgressLine }) {
  return (
    <li className="fos-compare-row">
      <div className="fos-compare-head">
        <strong>{name}</strong>
        <span className="fos-compare-amount">{formatMoney(line.raisedCents)}{line.goalCents ? <small> of {formatMoney(line.goalCents)}</small> : null}</span>
      </div>
      <ProgressBar raised={line.raisedCents} pending={line.pendingCents} outstanding={line.outstandingCents} goal={line.goalCents} />
      <div className="fos-compare-foot">
        <PercentChip line={line} />
        {line.pendingCents > 0 && <span>{formatMoney(line.pendingCents)} awaiting confirmation</span>}
        {line.outstandingCents > 0 && <span>{formatMoney(line.outstandingCents)} pledged, not yet received</span>}
      </div>
    </li>
  );
}

/** Council-wide viewers: the districts plus the money credited to the council itself add up to the council total. */
function Reconcile({ data }: { data: DashboardView }) {
  const { overall, councilCredit } = data;
  if (data.basis !== 'council' || !overall || !councilCredit) return null;
  const districts = data.districts.reduce((n, d) => n + d.raisedCents, 0);
  const other = overall.raisedCents - districts - councilCredit.raisedCents;
  const direct = councilCredit.raisedCents > 0 || councilCredit.pendingCents > 0 || councilCredit.outstandingCents > 0;
  return (
    <>
      {direct && (
        <ul className="fos-compare">
          <li className="fos-compare-row plain">
            <div className="fos-compare-head"><strong>Council (direct)</strong><span className="fos-compare-amount">{formatMoney(councilCredit.raisedCents)}</span></div>
            <div className="fos-compare-foot">
              <span>Credited to the council itself, not to a district. It counts toward the council goal.</span>
              {councilCredit.pendingCents > 0 && <span>{formatMoney(councilCredit.pendingCents)} awaiting confirmation</span>}
              {councilCredit.outstandingCents > 0 && <span>{formatMoney(councilCredit.outstandingCents)} pledged, not yet received</span>}
            </div>
          </li>
        </ul>
      )}
      <p className="fos-reconcile">
        Raised: districts <strong>{formatMoney(districts)}</strong> + Council (direct) <strong>{formatMoney(councilCredit.raisedCents)}</strong>
        {other !== 0 && <> + other <strong>{formatMoney(other)}</strong></>} = <strong>{formatMoney(overall.raisedCents)}</strong>
      </p>
    </>
  );
}

export default function FosDashboard() {
  const { context } = useFos();
  const [year, setYear] = useState('');
  const [campaign, setCampaign] = useState('');
  const [data, setData] = useState<DashboardView | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    fosFunds.dashboard({ campaignYear: year || null, campaignKey: campaign || null })
      .then((d) => { if (!cancelled) { setData(d); if (!year && d.selected.campaignYear) setYear(d.selected.campaignYear); } })
      .catch((cause) => { if (!cancelled) setError(fosError(cause).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year, campaign]);

  if (loading && !data) return <div className="fos-loading"><Spinner label="Loading progress" /></div>;
  if (error && !data) return <ErrorNote message={error} />;
  if (!data) return null;
  const { overall } = data;
  const noCampaign = data.years.length === 0;

  return (
    <>
      <div className="fos-toolbar">
        <label className="fos-inline">Campaign year
          <select value={year} onChange={(e) => setYear(e.target.value)} disabled={noCampaign}>{data.years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
        </label>
        <label className="fos-inline">Campaign
          <select value={campaign} onChange={(e) => setCampaign(e.target.value)} disabled={noCampaign}>
            <option value="">All campaigns</option>{data.campaigns.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
          </select>
        </label>
        <span className="fos-count subtitle">As of {formatDate(data.generatedOn)}</span>
      </div>
      <ErrorNote message={error} />

      {noCampaign && (
        <Section title="Start your fundraising year" icon="target">
          <EmptyState icon="target" title="No campaign has been set up yet">
            {context.capabilities.administerCampaigns ? <>Create a campaign and its campaign year, then set goals. <Link to="/fos/goals">Set up campaigns and goals</Link></> : 'A Friends of Scouting administrator needs to set up the campaign.'}
          </EmptyState>
        </Section>
      )}

      {overall && !noCampaign && (
        <Section title={data.basis === 'council' ? 'Council progress' : 'Your districts'} icon="chart" className="fos-hero-card">
          <div className="fos-hero-figure">
            <div>
              <span className="fos-kpi-label">Raised{data.selected.campaignKey ? '' : ' across campaigns'}</span>
              <div className="fos-big">{formatMoney(overall.raisedCents)}</div>
              <div className="subtitle">{overall.goalCents ? <>of a {formatMoney(overall.goalCents)} goal</> : <>No goal set yet{context.capabilities.administerGoals ? <> · <Link to="/fos/goals">set a goal</Link></> : null}</>}</div>
            </div>
            {overall.percent !== null && <div className="fos-ring" aria-label={`${overall.percent} percent of goal`}><strong>{overall.percent}%</strong><small>of goal</small></div>}
          </div>
          <ProgressBar raised={overall.raisedCents} pending={overall.pendingCents} outstanding={overall.outstandingCents} goal={overall.goalCents} tall />
          <Legend />
          <div className="fos-kpis">
            <Kpi label="Raised" value={formatMoney(overall.raisedCents)} hint="Confirmed by the council" tone="good" />
            <Kpi label="Awaiting confirmation" value={formatMoney(overall.pendingCents)} hint="Reported, not yet counted" tone="gold" />
            <Kpi label="Pledged, not yet received" value={formatMoney(overall.outstandingCents)} hint="Outstanding pledge balances" />
            <Kpi label="Donors giving" value={String(overall.donorsGiving)} hint="With a confirmed gift" tone="muted" />
          </div>
        </Section>
      )}

      {!noCampaign && (
        <div className="fos-two-col">
          <Section title="District comparison" icon="users">
            {data.districts.length === 0 ? <p className="fos-hint">No districts to compare.</p> : (
              <>
                <ul className="fos-compare">{data.districts.map((d) => <CompareRow key={d.districtId} name={d.name} line={d} />)}</ul>
                <Reconcile data={data} />
              </>
            )}
          </Section>
          <Section title="Campaign performance" icon="target">
            {data.campaignPerformance.length === 0 ? <p className="fos-hint">No campaign has a {data.selected.campaignYear} year.</p> : (
              <ul className="fos-compare">{data.campaignPerformance.map((c) => <CompareRow key={c.key} name={`${c.name}${c.primary ? ' · primary' : ''}`} line={c} />)}</ul>
            )}
          </Section>
        </div>
      )}

      {!noCampaign && !data.hasAnyGoal && context.capabilities.administerGoals && (
        <p className="fos-hint"><Icon name="target" size={13} /> No goals are set for this selection. <Link to="/fos/goals">Set goals</Link> to see percent of goal.</p>
      )}
    </>
  );
}
